import type {
  StylesConfig as SelectStylesConfig,
  Theme as SelectTheme,
  ThemeConfig as SelectThemeConfig,
} from 'react-select';
import styled from '@emotion/styled';
import type { Theme as LocalTheme } from '@trinity/chat-components/types/theme';

export const Row = styled.div({
  display: 'flex',
  boxSizing: 'border-box',

  '&:not(:last-child)': {
    marginBottom: '10px',
  },
});

export const Label = styled.div({
  display: 'flex',
  alignItems: 'center',
  boxSizing: 'border-box',
  fontSize: '14px',
  marginRight: '12px',
  width: '110px',
});

export const Value = styled.div({
  display: 'flex',
  alignItems: 'center',
  boxSizing: 'border-box',
  flex: '1',
});

export function buildSelectStyles(theme: LocalTheme.Theme): SelectStylesConfig<unknown> {
  return {
    menu: base => ({
      ...base,
      fontSize: '14px',
      fontFamily: theme.fonts.regular,
      width: 'max-content',
      minWidth: '100%',
      zIndex: 2,
    }),
    option: (base, state) => {
      return {
        ...base,
        fontFamily: theme.fonts.regular,
        color: (() => {
          if (state.isSelected && !state.isFocused) return theme.palette.text.primary;
          if (state.isFocused) return theme.palette.wl.enabled ? theme.palette.wl.primary.main : theme.palette.hyperlink;
          return undefined;
        })(),
        backgroundColor: (() => {
          if (state.isFocused) return theme.palette.wl.enabled ? theme.palette.wl.prompt.light6 : theme.palette.blue.light3;
          return theme.palette.white.main;
        })(),
        '&:active': {
          backgroundColor: (() => {
            if (state.isFocused) return theme.palette.wl.enabled ? theme.palette.wl.prompt.light6 : theme.palette.blue.light3;
            return theme.palette.white.main;
          })(),
        },
      };
    },
    container: base => ({
      ...base,
      width: '100%',
    }),
    valueContainer: base => ({
      ...base,
      maxHeight: '400px',
      overflowY: 'auto',
    }),
    singleValue: base => ({
      ...base,
      fontSize: '14px',
      fontFamily: theme.fonts.regular,
    }),
    multiValue: base => ({
      ...base,
      fontFamily: theme.fonts.regular,
    }),
    multiValueLabel: base => ({
      ...base,
      fontSize: '14px',
    }),
    multiValueRemove: (base, state) => ({
      ...base,
      fontSize: '14px',
      display: state.isDisabled ? 'none' : base.display,
    }),
    placeholder: base => ({
      ...base,
      fontSize: '14px',
      fontFamily: theme.fonts.regular,
    }),
  };
}

export function buildSelectTheme(theme: LocalTheme.Theme): SelectThemeConfig {
  return (selectTheme: SelectTheme) => {
    return {
      ...selectTheme,
      colors: {
        ...selectTheme.colors,
        primary: theme.palette.primary.main,
        primary25: theme.palette.primary.light4,
        primary50: theme.palette.primary.light3,
        primary75: theme.palette.primary.light2,
        danger: theme.palette.red.main,
      },
    };
  };
}